// The browser client for the App controls' discovery: what the selected host (artifact/revision/version/purpose
// only) cannot know. One read of GET /api/v1/company/apps/control-selection/:artifactId/:versionId over the
// session-bound fetch; it grants nothing (budget and schedule authorize themselves). The answer is exactly shaped
// or it is "unavailable"; nothing is guessed, and a job's `label` is only ever the DECLARED label (null when the
// App declares none). `name` is an identifier and is never shown as a title.
import {
  parseAppSchedulePresentation,
  type AppSchedulePresentation,
} from './schedule-presentation';
export {
  parseAppSchedulePresentation,
  type AppSchedulePresentation,
} from './schedule-presentation';
export type ScheduledJob = {
  id: string;
  name: string;
  label: string | null;
  /** An exact supported cadence of this version; unknown cron is null. */
  schedule: AppSchedulePresentation | null;
};
export type WriteKind = 'insert' | 'update' | 'delete';
/** A declared function that asks to change saved data, with exactly the changes its declaration names. */
export type WritingFunction = { id: string; name: string; writes: WriteKind[] };
export type DeclaredCollection = { id: string; name: string | null };
export type ControlSelection = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  /** The genuine logical App id of the selected Saved5 (the schedule/budget services' `appId`). */
  appId: string;
  jobs: ScheduledJob[];
  /** Declared functions that change data, and the App's declared collections (owner setup of data writes). */
  functions: WritingFunction[];
  collections: DeclaredCollection[];
};

const PREFIX = '/api/v1/company/apps/control-selection';
const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const text = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null);

export function parseControlSelection(value: unknown): ControlSelection | null {
  const raw = obj(value);
  const [artifactId, versionId, revisionId, appId] = [
    text(raw?.artifactId),
    text(raw?.versionId),
    text(raw?.revisionId),
    text(raw?.appId),
  ];
  if (!raw || !artifactId || !versionId || !revisionId || !appId || !Array.isArray(raw.jobs))
    return null;
  const jobs: ScheduledJob[] = [];
  for (const entry of raw.jobs) {
    const job = obj(entry);
    const id = text(job?.id);
    const name = text(job?.name);
    if (!job || !id || !name) return null;
    if (job.label !== null && typeof job.label !== 'string') return null;
    const declared =
      job.schedule === undefined || job.schedule === null
        ? null
        : parseAppSchedulePresentation(job.schedule);
    if (job.schedule !== undefined && job.schedule !== null && !declared) return null;
    jobs.push({
      id,
      name,
      label: job.label as string | null,
      schedule: declared,
    });
  }
  if (new Set(jobs.map((job) => job.id)).size !== jobs.length) return null;
  const kinds: readonly WriteKind[] = ['insert', 'update', 'delete'];
  const functions: WritingFunction[] = [];
  for (const entry of Array.isArray(raw.functions) ? raw.functions : []) {
    const fn = obj(entry);
    const id = text(fn?.id);
    const name = text(fn?.name);
    const writes = Array.isArray(fn?.writes) ? fn.writes : null;
    if (
      !fn ||
      !id ||
      !name ||
      !writes ||
      !writes.every((kind) => kinds.includes(kind as WriteKind))
    )
      return null;
    functions.push({ id, name, writes: writes as WriteKind[] });
  }
  const collections: DeclaredCollection[] = [];
  for (const entry of Array.isArray(raw.collections) ? raw.collections : []) {
    const collection = obj(entry);
    const id = text(collection?.id);
    if (!collection || !id) return null;
    collections.push({ id, name: text(collection.name) });
  }
  return { artifactId, versionId, revisionId, appId, jobs, functions, collections };
}

export function createControlSelectionApi(
  artifactId: string,
  versionId: string,
  send: typeof fetch,
): { read(): Promise<ControlSelection> } {
  const url = `${PREFIX}/${encodeURIComponent(artifactId)}/${encodeURIComponent(versionId)}`;
  return {
    async read() {
      const response = await send(url, { credentials: 'include' });
      let body: unknown = null;
      try {
        body = response.status === 200 ? await response.json() : null;
      } catch {
        body = null;
      }
      const selection = parseControlSelection(body);
      // It must be the very selection that was asked for, or it is not an answer to it.
      if (!selection || selection.artifactId !== artifactId || selection.versionId !== versionId)
        throw new Error('control_selection_unavailable');
      return selection;
    },
  };
}
